import {
  CommonConfigComponent,
  IWidgetComponentProps,
  StyledDrawerCollapse,
} from '@framework/engine';
import {
  CheckboxInput,
  DecimalInput,
  FormItem,
  RadioGroupInput,
  SelectInput,
  TextInput,
} from '@framework/form';
import { RowElement } from '@framework/ui';
import { Collapse } from 'antd';
import React from 'react';
import { IMapWidget, MapWidgetSelectionShape } from './IMapWidget';
import { MapWidgetLogic } from './MapWidgetLogic';
import { MapExtentInput } from './config/MapExtentInput';
import { MapLayerURLInput } from './config/MapLayerURLInput';
import { MapLayersConfigSection } from './config/MapLayersConfigSection';
import { MapPrintImagesConfigSection } from './config/print/MapPrintImagesConfigSection';
import { MapLayerTypeOptions } from './layers/MapLayer';

export interface IMapWidgetConfigComponentProps
  extends IWidgetComponentProps<IMapWidget> {}

export function MapWidgetConfigComponent(
  props: IMapWidgetConfigComponentProps,
) {
  return (
    <StyledDrawerCollapse
      defaultActiveKey={['general', 'selection', 'map', 'layers']}
      ghost
    >
      <Collapse.Panel header="Impostazioni Generali" key="general">
        <div>
          <CommonConfigComponent
            {...props}
            hideConfigPlaceholder
            hideConfigDefaultValue
          />
        </div>
      </Collapse.Panel>

      <Collapse.Panel header="Mappa" key="map">
        <FormItem
          name={'position'}
          label="Posizionamento della mappa"
          help="Trascina la mappa per individuare il centro e il livello di zoom desiderato, quindi premi su 'Seleziona centro e zoom' per salvare l'informazione. La mappa verrà presentata all'utente compilatore secondo i critieri indicati."
        >
          <MapExtentInput name="position" />
        </FormItem>
      </Collapse.Panel>

      <Collapse.Panel header="Selezione" key="selection">
        <RowElement>
          <FormItem
            name="selection.multiple"
            label="Selezione multipla"
            help="Se abilitato, l'utente potrà selezionare più geometrie contemporaneamente."
          >
            <CheckboxInput name="selection.multiple">
              Abilita selezione multipla
            </CheckboxInput>
          </FormItem>
          <FormItem name={'selection.shape'} label="Geometrie Selezionabili">
            <RadioGroupInput
              name="selection.shape"
              options={[
                { label: 'Punti', value: MapWidgetSelectionShape.Point },
                { label: 'Poligoni', value: MapWidgetSelectionShape.Polygon },
              ]}
            />
          </FormItem>
        </RowElement>

        <RowElement>
          <FormItem
            name="selection.hideGeometriesTable"
            label="Nascondi tabella geometrie"
            help="Se abilitato, la tabella delle geometrie selezionate verrà nascosta."
          >
            <CheckboxInput name="selection.hideGeometriesTable">
              Nascondi tabella geometrie
            </CheckboxInput>
          </FormItem>
        </RowElement>
      </Collapse.Panel>

      <Collapse.Panel header="Livelli" key="layers">
        <MapLayersConfigSection name="layers" />
      </Collapse.Panel>

      {MapWidgetLogic.showCadastralParticelsAndIntersections() && (
        <Collapse.Panel header="Intersezione" key="intersection">
          <RowElement>
            <FormItem
              name="intersection.enabled"
              label="Intersezione abilitata"
              help={
                <span>
                  Se abilitato, le geometrie disegnate dall&apos;utente verranno
                  confrontate con il Layer indicato. Verranno quindi considerate
                  le intersezioni con il layer. Richiede la selezione di{' '}
                  <strong>Poligoni</strong>.
                </span>
              }
            >
              <CheckboxInput name="intersection.enabled">
                Abilita intersezione
              </CheckboxInput>
            </FormItem>
          </RowElement>
          <FormItem
            label="Titolo"
            name={`intersection.title`}
            help="Titolo della tabella delle intersezioni calcolate"
          >
            <TextInput name={`intersection.title`} placeholder="Intersezioni" />
          </FormItem>
          <FormItem label="Tipo" name={`intersection.type`}>
            <SelectInput
              name={`intersection.type`}
              options={MapLayerTypeOptions.filter(l => l.allowIntersect)}
            />
          </FormItem>
          <FormItem label="URL" name={`intersection.url`}>
            <TextInput name={`intersection.url`} />
          </FormItem>
          <FormItem
            label="Tolleranza (metri quadri minimi)"
            name={`intersection.areaTolerance`}
            help="Area minima di tolleranza per considerare un'intersezione valida"
          >
            <DecimalInput
              name={`intersection.areaTolerance`}
              placeholder="0,05mq"
            />
          </FormItem>
        </Collapse.Panel>
      )}

      <Collapse.Panel header="Competenze" key="competence">
        <FormItem
          name="competence.enabled"
          label="Riconoscimento Competenza"
          help={
            <div>
              <p>
                Se abilitato, le geometrie disegnate dall&apos;utente verranno
                confrontate con quelle presenti nel layer vettoriale
                configurato. Verrà selezionata automaticamente la Competenza con
                area maggiore. Richiede la selezione di{' '}
                <strong>Poligoni</strong>.
              </p>
            </div>
          }
        >
          <CheckboxInput name="competence.enabled">
            Abilita riconoscimento Competenza
          </CheckboxInput>
        </FormItem>
        <FormItem label="Tipo" name={`competence.type`}>
          <SelectInput
            name={`competence.type`}
            options={MapLayerTypeOptions.filter(l => l.allowIntersect)}
          />
        </FormItem>
        <FormItem label="URL" name={`competence.url`}>
          <MapLayerURLInput
            name={`competence.url`}
            typeFieldName="competence.type"
          />
        </FormItem>
        <FormItem
          name="competence.propertyName"
          label="Nome proprietà per Identificare le Competenze"
          help="Il nome della proprietà che contiene il codice della competenza all'interno delle geometrie del layer indicato."
        >
          <TextInput name="competence.propertyName" />
        </FormItem>
      </Collapse.Panel>

      <Collapse.Panel header="Immagini di Stampa" key="print">
        <MapPrintImagesConfigSection name="printImages" />
      </Collapse.Panel>
    </StyledDrawerCollapse>
  );
}
